import { useTranslation } from "react-i18next";
import { Ban } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NeuCard } from "../../components/neu/Neu";
import { listSalaryHolds, releaseSalaryHold, scanSalaryHolds } from "../../api/salaryHolds";
import { HoldsToolbar } from "./HoldsToolbar";
import { HoldRow } from "./HoldRow";

const EMPTY = { items: [], branch_rules: [], last_scan_at: null };

// تبويب «الرواتب الموقوفة»: بيانات مُخزَّنة مؤقتاً (react-query) فلا إقلاع متكرر عند التنقل.
export const SalaryHoldsTab = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data = EMPTY, isFetching, refetch } = useQuery({
    queryKey: ["salary-holds"],
    queryFn: listSalaryHolds,
    staleTime: 120_000,
    placeholderData: (prev) => prev,
  });

  const run = useMutation({
    onSuccess: () => qc.invalidateQueries({ queryKey: ["salary-holds"] }),
    onError: () => toast.error(t("errors.generic")),
    mutationFn: async ({ fn }) => fn(),
  });

  const act = (fn, done) => run.mutate({ fn }, { onSuccess: (res) => {
    qc.invalidateQueries({ queryKey: ["salary-holds"] });
    toast.success(done(res));
  } });

  return (
    <div className="grid gap-5" data-testid="salary-holds-tab">
      <HoldsToolbar
        lastScan={data.last_scan_at}
        rules={data.branch_rules || []}
        busy={run.isPending || isFetching}
        onReload={refetch}
        onScan={() => act(scanSalaryHolds, (r) =>
          r.auto_released
            ? t("holds.scanDoneReleased", { count: r.held, released: r.auto_released })
            : r.warned
              ? t("holds.scanDoneWarned", { count: r.held, warned: r.warned })
              : t("holds.scanDone", { count: r.held }))}
      />
      <NeuCard className="overflow-hidden p-0">
        <div className="flex items-center gap-2 bg-red-600 px-5 py-3 text-white">
          <Ban className="h-5 w-5" />
          <p className="font-black">{t("holds.listTitle", { count: data.items.length })}</p>
        </div>
        {data.items.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground" data-testid="holds-empty">
            {t("holds.empty")}
          </p>
        ) : (
          data.items.map((item) => (
            <HoldRow key={item.id} item={item} busy={run.isPending}
                     onRelease={(id) => act(() => releaseSalaryHold(id), () => t("holds.released"))} />
          ))
        )}
      </NeuCard>
    </div>
  );
};
